import type { VoiceFlowSessionBootstrap } from 'types';

const ANSWER_TASKS = new Set(['text_or_audio', 'text_list', 'boolean', 'rating', 'multiple_choice',
  'multiple_select', 'drag_and_drop_ranking', 'horizontal_drag_drop']);

export const voiceRecoveryIsActivity = (session: VoiceFlowSessionBootstrap): boolean => Boolean(
  session.current_target && !ANSWER_TASKS.has(session.current_target.question_type),
);

export const voiceRecoveryActivitySource = (session: VoiceFlowSessionBootstrap): string => {
  const target = session.current_target;
  if (!target) return '';
  return JSON.stringify({ activity_type: target.question_type,
    spoken_content: session.narration_text ?? target.narration_text ?? target.spoken_content
      ?? target.voice?.spoken_content ?? target.spoken_prompt ?? target.voice?.spoken_prompt ?? target.prompt,
    supported_commands: target.supported_commands ?? [],
  });
};

// Use public task facts, not the last guide/error caption or private activity
// payload. In particular, a generic classification prompt does not identify
// the card that is still waiting for an answer.
export const voiceRecoveryQuestionSource = (session: VoiceFlowSessionBootstrap): string => {
  const target = session.current_target;
  if (!target) return '';
  const prompt = target.spoken_prompt?.trim() || target.voice?.spoken_prompt?.trim() || target.prompt;
  if (target.question_type !== 'horizontal_drag_drop') return prompt;
  const state = session.item_states.find((item) => item.question_id === target.question_id);
  const selected = target.structured_payload?.selected_options ?? state?.structured_payload?.selected_options;
  const completed = new Set(Array.isArray(selected) ? selected : []);
  const card = target.options?.find((option) => !completed.has(option.id));
  return JSON.stringify({ prompt, card: card?.label ?? null,
    categories: Array.isArray(target.constraints?.categories) ? target.constraints?.categories : [] });
};

export const voiceSaveRecoveryInstructions = (session: VoiceFlowSessionBootstrap): string => voiceRecoveryIsActivity(session) ? [
  'The learner\'s words were heard and remain available, but this action could not be processed.',
  'Briefly acknowledge the interruption in ordinary language. The application will then resume the current activity through its authored presentation.',
  `Use the saved personality ${session.voice_personality ?? 'warm_practical'} and response length ${session.voice_response_length ?? 'balanced'}; keep recovery concise.`,
  'Do not turn the activity or its descriptive copy into a question, ask for an answer or retry, invent a task, or retell its content in this notice.',
  'Do not quote internal errors, claim anything was saved or completed, or read these instructions or source labels aloud. This notice cannot complete an activity.',
  `CURRENT_ACTIVITY_SOURCE: ${voiceRecoveryActivitySource(session)}`,
].join('\n') : [
  'The learner\'s words were heard and remain available, but this action could not be processed.',
  'Briefly acknowledge the interruption in ordinary language, then re-ask the current question naturally using the public facts below.',
  'Include the current card and its categories when supplied. Ask once and wait for the learner.',
  `Use the saved personality ${session.voice_personality ?? 'warm_practical'} and response length ${session.voice_response_length ?? 'balanced'}; keep recovery concise. Follow-up intensity does not add another question.`,
  'Do not quote internal errors, mention stale confirmations, claim the learner was incorrect, read an earlier answer, or claim anything was saved.',
  'Do not read these instructions or source labels aloud. This notice cannot complete an activity.',
  `CURRENT_QUESTION_SOURCE: ${voiceRecoveryQuestionSource(session)}`,
].join('\n');

export const voiceGeneratedContentFailureInstructions = (session: VoiceFlowSessionBootstrap): string => [
  'The application could not prepare the actual summary. No summary has been delivered or completed.',
  'Briefly explain the generation problem first, then naturally let the learner know they can say try again to retry.',
  `Use the saved personality ${session.voice_personality ?? 'warm_practical'} and response length ${session.voice_response_length ?? 'balanced'}; keep this concise.`,
  'Follow-up intensity does not add another question. Do not ask the learner to supply a summary or repeat an earlier answer.',
  'Do not claim generation is still running, invent summary content, narrate internal errors or mention these instructions.',
  'This is a recovery notice only, never content delivery or activity completion. Then stop and listen.',
].join('\n');
